import type { CalendarDate } from "@internationalized/date";
import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { useContext } from "react";
import { useDateFormatter } from "react-aria";

import {
  Calendar as AriaCalendar,
  CalendarCell as AriaCalendarCell,
  CalendarGrid as AriaCalendarGrid,
  CalendarGridHeader as AriaCalendarGridHeader,
  CalendarGridBody,
  CalendarHeaderCell,
  CalendarStateContext,
  Heading,
  RangeCalendarStateContext,
  Text,
  useLocale,
  type CalendarProps as AriaCalendarProps,
  type CalendarCellProps,
  type CalendarGridProps,
  type DateValue,
} from "react-aria-components";

import { cn } from "@beweegbaas/ui/lib/utils";
import { Button } from "./button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./select";
import { calendarCellStyles } from "./range-calendar";

export interface CalendarProps<T extends DateValue> extends Omit<
  AriaCalendarProps<T>,
  "visibleDuration"
> {
  errorMessage?: string;
  monthAriaLabel?: string;
  yearAriaLabel?: string;
}

export function Calendar<T extends DateValue>({
  errorMessage,
  monthAriaLabel,
  yearAriaLabel,
  ...props
}: CalendarProps<T>) {
  return (
    <AriaCalendar {...props}>
      <CalendarHeaderWithDropdowns monthAriaLabel={monthAriaLabel} yearAriaLabel={yearAriaLabel} />
      <AriaCalendarGrid>
        <CalendarGridHeader />
        <CalendarGridBody>{(date) => <CalendarCell date={date} />}</CalendarGridBody>
      </AriaCalendarGrid>
      {errorMessage && (
        <Text slot="errorMessage" className="text-sm text-red-600">
          {errorMessage}
        </Text>
      )}
    </AriaCalendar>
  );
}

export function CalendarCell({ className, ...props }: CalendarCellProps) {
  return (
    <AriaCalendarCell
      {...props}
      className={cn(
        `group outside-month:text-neutral-300 selected:bg-blue-100 dark:selected:bg-blue-700/30 forced-colors:selected:bg-[Highlight] invalid:selected:bg-red-100 dark:invalid:selected:bg-red-700/30
        forced-colors:invalid:selected:bg-[Mark] [&[data-unavailable]]:text-muted-foreground my-px
        aspect-square size-9
        cursor-default rounded-full
        text-sm outline
        outline-0
        [-webkit-tap-highlight-color:transparent]
        [&[data-unavailable]]:hover:bg-transparent
        [&[data-unavailable]]:dark:hover:bg-transparent`,
        className,
      )}
    >
      {({ formattedDate, isSelected, isFocusVisible, isDisabled, isUnavailable }) => (
        <span
          className={cn(
            calendarCellStyles({
              selectionState: isSelected ? "cap" : "none",
              isDisabled,
              isFocusVisible,
            }),
            isUnavailable &&
              `text-muted-foreground/50 group-hover:bg-transparent
              dark:group-hover:bg-transparent`,
          )}
        >
          {formattedDate}
        </span>
      )}
    </AriaCalendarCell>
  );
}

export function CalendarGrid({ className, ...props }: CalendarGridProps) {
  return <AriaCalendarGrid {...props} className={cn(className)} />;
}

export function CalendarHeader() {
  const state = useContext(CalendarStateContext);
  const rangeState = useContext(RangeCalendarStateContext);
  let { direction } = useLocale();
  return (
    <header className="grid w-full grid-cols-7 items-center pb-4">
      <Button
        size="icon"
        variant="secondary"
        className="justify-self-center"
        onClick={() => {
          state?.focusPreviousPage();
          rangeState?.focusPreviousPage();
        }}
      >
        {direction === "rtl" ? (
          <IconChevronRight aria-hidden className="size-4" />
        ) : (
          <IconChevronLeft aria-hidden className="size-4" />
        )}
      </Button>
      <Heading className="col-span-5 mb-0 text-center text-base font-semibold" />
      <Button
        size="icon"
        variant="secondary"
        className="justify-self-center"
        onClick={() => {
          state?.focusNextPage();
          rangeState?.focusNextPage();
        }}
      >
        {direction === "rtl" ? (
          <IconChevronLeft aria-hidden className="size-4" />
        ) : (
          <IconChevronRight aria-hidden className="size-4" />
        )}
      </Button>
    </header>
  );
}

interface MonthItem {
  id: number;
  date: CalendarDate;
  formatted: string;
}

export function CalendarMonthDropdown() {
  const calendarState = useContext(CalendarStateContext);
  const rangeCalendarState = useContext(RangeCalendarStateContext);
  const state = calendarState ?? rangeCalendarState!;
  const formatter = useDateFormatter({
    month: "short",
    timeZone: state.timeZone,
  });

  const months: MonthItem[] = [];
  const numMonths = state.focusedDate.calendar.getMonthsInYear(state.focusedDate);
  for (let i = 1; i <= numMonths; i++) {
    const date = state.focusedDate.set({ month: i });
    months.push({
      id: i,
      date,
      formatted: formatter.format(date.toDate(state.timeZone)),
    });
  }

  return (
    <Select
      value={String(state.focusedDate.month)}
      onValueChange={(value) => {
        if (value) {
          const month = Number.parseInt(value, 10);
          const item = months.find((monthItem) => monthItem.id === month);
          if (item) {
            state.setFocusedDate(item.date);
          }
        }
      }}
    >
      <SelectTrigger aria-label="Month" className="w-22 shrink-0 gap-2" size="sm">
        <SelectValue>{formatter.format(state.focusedDate.toDate(state.timeZone))}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {months.map((item) => (
          <SelectItem key={item.id} value={String(item.id)}>
            {item.formatted}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface YearItem {
  id: number;
  date: CalendarDate;
  formatted: string;
}

export function CalendarYearDropdown() {
  const calendarState = useContext(CalendarStateContext);
  const rangeCalendarState = useContext(RangeCalendarStateContext);
  const state = calendarState ?? rangeCalendarState!;
  const formatter = useDateFormatter({
    year: "numeric",
    timeZone: state.timeZone,
  });

  const years: YearItem[] = [];
  for (let i = -20; i <= 20; i++) {
    const date = state.focusedDate.add({ years: i });
    years.push({
      id: years.length,
      date,
      formatted: formatter.format(date.toDate(state.timeZone)),
    });
  }

  const selectedIndex = years.findIndex(
    (y) => y.date.year === state.focusedDate.year && y.date.month === state.focusedDate.month,
  );
  const selectedKey = selectedIndex >= 0 ? String(selectedIndex) : "20";

  return (
    <Select
      value={selectedKey}
      onValueChange={(value) => {
        if (value) {
          const index = Number.parseInt(value, 10);
          const item = years[index];
          if (item) {
            state.setFocusedDate(item.date);
          }
        }
      }}
    >
      <SelectTrigger aria-label="Year" className="w-22 shrink-0 gap-2" size="sm">
        <SelectValue>{formatter.format(state.focusedDate.toDate(state.timeZone))}</SelectValue>
      </SelectTrigger>
      <SelectContent>
        {years.map((item) => (
          <SelectItem key={item.id} value={String(item.id)}>
            {item.formatted}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export interface CalendarHeaderWithDropdownsProps {
  monthAriaLabel?: string;
  yearAriaLabel?: string;
}

export function CalendarHeaderWithDropdowns(_props: CalendarHeaderWithDropdownsProps = {}) {
  const state = useContext(CalendarStateContext);
  const rangeState = useContext(RangeCalendarStateContext);
  const { direction } = useLocale();
  return (
    <header className="grid w-full grid-cols-7 items-center pb-4">
      <Button
        size="icon"
        variant="secondary"
        className="justify-self-center"
        onClick={() => {
          state?.focusPreviousPage();
          rangeState?.focusPreviousPage();
        }}
      >
        {direction === "rtl" ? (
          <IconChevronRight aria-hidden className="size-4" />
        ) : (
          <IconChevronLeft aria-hidden className="size-4" />
        )}
      </Button>
      <div className="col-span-5 flex items-center justify-center gap-1">
        <CalendarMonthDropdown />
        <CalendarYearDropdown />
      </div>
      <Button
        size="icon"
        variant="secondary"
        className="justify-self-center"
        onClick={() => {
          state?.focusNextPage();
          rangeState?.focusNextPage();
        }}
      >
        {direction === "rtl" ? (
          <IconChevronLeft aria-hidden className="size-4" />
        ) : (
          <IconChevronRight aria-hidden className="size-4" />
        )}
      </Button>
    </header>
  );
}

export function CalendarGridHeader() {
  return (
    <AriaCalendarGridHeader>
      {(day) => (
        <CalendarHeaderCell className="text-xs font-semibold text-gray-500">
          {day}
        </CalendarHeaderCell>
      )}
    </AriaCalendarGridHeader>
  );
}
